import { useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { useMutation } from '@tanstack/react-query';

import { ActionButton } from 'commonComponents/ActionButton';

import { deactivateExpiredLicenseFilesWithSameSerialNumber } from 'api/licenses';
import { CreateIcon } from 'icons/CreateIcon';
import { ROLE_DEFINITIONS } from 'roles/definitions';
import { RoleDependentComponent } from 'roles/RoleDependentComponent';
import { ROUTES } from 'routes/routes';

export function CreateLicenseFileButton({ licenseDetails }) {
    const navigate = useNavigate();
    const { id: licenseId, invalidated } = licenseDetails;

    const { mutate: trigger, isPending: isMutating } = useMutation({
        mutationFn: () => deactivateExpiredLicenseFilesWithSameSerialNumber(licenseId),
        onSuccess: () => {
            const route = generatePath(ROUTES.LICENSEFILE_CREATE, { licenseId });
            navigate(route);
        },
        meta: { errorMessage: 'Deactivating license file failed' },
    });

    const [open, setOpen] = useState(false);

    if (invalidated) {
        return null;
    }

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    return licenseDetails.maximumActivations &&
        licenseDetails.activeLicenseFilesCount >= licenseDetails.maximumActivations ? (
        <>
            <Button color="primary" size="large" variant="contained" onClick={handleClickOpen}>
                Activate License
            </Button>
            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>Failed to Activate License</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        You have already activated this license on the maximum number of devices.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} autoFocus>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    ) : (
        <RoleDependentComponent
            userRole={ROLE_DEFINITIONS.Customer}
            component={ActionButton}
            iconClass={CreateIcon}
            onClick={() => trigger()}
            disabled={isMutating}
            inProgress={isMutating}
            title="Activate License"
            variant="contained"
            data-testid="LicenseDetails_CreateLicenseFile"
        />
    );
}
